/**
 * OLMPS Spine — MoyRi gear-grouped intake bank.
 *
 * Five tables (one per gear), 20 questions each, per docs/OLMPS_SPINE_SPEC.md §3.
 * Question order: 1-3 broadest discriminators (may be all the user answers),
 * 1-10 a balanced baseline touching every tool/malady in the gear at least
 * twice, 11-20 sharpen accuracy. Options are 3-4 wide, scored -3..+3
 * (negative = malady-leaning, positive = trained behaviour, one ~0 neutral).
 * `leans` values are real `${toolId}:${maladyId}` pairs pulled from the DMT
 * registry in spectrum-tools.ts — do not invent ids here.
 *
 * See spine-types.ts for the shared contracts (do not fork them).
 */
import type { Gear } from '../domain/olmps';
import { GEAR_DEFAULT_DMT, type GearIntakeOption, type GearIntakeQuestion, type GearIntakeResult } from './spine-types';
import { DMT_TOOLS, normalizeMaladyId } from './spectrum-tools';

const q = (prompt: string, options: GearIntakeOption[]): GearIntakeQuestion => ({ prompt, options });

// ── ORIENT — TELOS (TYTN) diagnoses plane (Planē); ORACLE (DEMI) has amathia ──
const ORIENT_QUESTIONS: GearIntakeQuestion[] = [
  q('When you pick what to work on next, it usually traces back to…', [
    { label: "whatever's loudest in front of me", score: -3, leans: 'telos:plane' },
    { label: 'a rough sense of my priorities', score: 0 },
    { label: 'a declared direction I can name', score: 3 },
  ]),
  q('If someone asked why you did today what you did, you could…', [
    { label: "reconstruct almost none of it — it just happened", score: -3, leans: 'telos:plane' },
    { label: 'give a rough story', score: 0 },
    { label: 'trace every choice back to a north star', score: 3 },
  ]),
  q('A new request lands mid-day. You…', [
    { label: 'drop what you were doing without checking if it fits', score: -3, leans: 'telos:plane' },
    { label: 'weigh it against your day, sometimes', score: 0 },
    { label: 'check it against the direction before saying yes', score: 3 },
  ]),
  q('Your tasks and projects carry a life-sector link (Social/Health/Admin/Personal/Economic)…', [
    { label: 'almost never — nothing is tagged to anything', score: -2, leans: 'telos:plane' },
    { label: 'some do', score: 0 },
    { label: 'as a rule, everything traces to a sector', score: 2 },
  ]),
  q('Saying no to something that does not serve your direction is…', [
    { label: "something I don't really do — I say yes to keep the peace", score: -2, leans: 'telos:plane' },
    { label: 'possible, if I remember to check', score: 0 },
    { label: 'automatic once I see the mismatch', score: 2 },
  ]),
  q('Two weeks from now, could you predict what you will be working on?', [
    { label: 'no — it will be whatever shows up that week', score: -3, leans: 'telos:plane' },
    { label: 'roughly the shape of it', score: 0 },
    { label: 'yes, it is already filtered through the plan', score: 3 },
  ]),
  q('Mid-task, your attention drifts to something unrelated to any goal…', [
    { label: 'constantly, and I rarely notice until later', score: -3, leans: 'telos:plane' },
    { label: 'sometimes, and I catch it eventually', score: 0 },
    { label: 'rarely — I notice fast and correct', score: 2 },
  ]),
  q('When you block time on the calendar, the reason for the block is…', [
    { label: "usually unclear, even to me, an hour later", score: -2, leans: 'telos:plane' },
    { label: 'roughly known', score: 0 },
    { label: 'always traceable to a declared aim', score: 2 },
  ]),
  q('Competing invitations for the same slot — you resolve it by…', [
    { label: 'whichever feels most urgent in the moment', score: -3, leans: 'telos:plane' },
    { label: 'gut feel, mostly', score: 0 },
    { label: 'which one actually serves the direction', score: 3 },
  ]),
  q('At year-end, your projects from January to December would read as…', [
    { label: 'a scattered pile with no throughline', score: -3, leans: 'telos:plane' },
    { label: 'a mixed bag — some connect, some do not', score: 0 },
    { label: 'one continuous story pointed at the same north star', score: 3 },
  ]),
  q('Your morning starts with…', [
    { label: 'checking what is on fire', score: -2, leans: 'telos:plane' },
    { label: 'a loose mental list', score: 0 },
    { label: 'a look at what actually moves the direction forward', score: 2 },
  ]),
  q('When a goal you set months ago comes back into view, you…', [
    { label: "can't remember why you set it", score: -2, leans: 'telos:plane' },
    { label: 'half-remember the reasoning', score: 0 },
    { label: 'still see exactly why it matters', score: 2 },
  ]),
  q('Delegating or dropping a task — the decision is driven by…', [
    { label: 'whatever is easiest to get off my plate', score: -2, leans: 'telos:plane' },
    { label: 'a mix of ease and importance', score: 0 },
    { label: 'whether it serves the direction at all', score: 2 },
  ]),
  q('A shiny new opportunity shows up that has nothing to do with your goals…', [
    { label: 'I chase it anyway, most of the time', score: -3, leans: 'telos:plane' },
    { label: "I'm tempted but sometimes hold the line", score: 0 },
    { label: 'I screen it against the north star first', score: 2 },
  ]),
  q('If your endeavour statement disappeared tomorrow, your week would…', [
    { label: 'look identical — it was not actually driving anything', score: -3, leans: 'telos:plane' },
    { label: 'shift a little', score: 0 },
    { label: 'fall apart, because it was load-bearing', score: 3 },
  ]),
  q('When two of your own goals quietly conflict, you…', [
    { label: 'never notice — you just work on whatever is in front of you', score: -2, leans: 'telos:plane' },
    { label: 'notice eventually', score: 0 },
    { label: 'catch it early and resolve which wins', score: 2 },
  ]),
  q('Evenings, you can say whether today served the direction or not…', [
    { label: 'no idea — the day just happened', score: -2, leans: 'telos:plane' },
    { label: 'a vague sense either way', score: 0 },
    { label: 'yes, clearly, every night', score: 2 },
  ]),
  q('A long, slow-payoff goal versus a short, loud task — which wins your time?', [
    { label: 'the loud one, almost always', score: -2, leans: 'telos:plane' },
    { label: 'depends on the day', score: 0 },
    { label: 'whichever the direction actually calls for', score: 2 },
  ]),
  q('When you explain a decision to someone else, the explanation is…', [
    { label: "usually made up after the fact — the real reason was just habit", score: -2, leans: 'telos:plane' },
    { label: 'roughly true', score: 0 },
    { label: 'the same reasoning that drove the choice in real time', score: 2 },
  ]),
  q('Overall, right now, your sense of direction feels…', [
    { label: 'like there is no real one — I drift from thing to thing', score: -3, leans: 'telos:plane' },
    { label: 'present but fuzzy', score: 0 },
    { label: 'sharp and something I could recite', score: 3 },
  ]),
];

// ── REGULATE — NUMA (oizys/phthisis/aergia), MoyRi (akrasia/kakia/adrasteia), HRMEZ (homados/oistros)
const REGULATE_QUESTIONS: GearIntakeQuestion[] = [
  q('On a demanding day, what actually happens to you is closer to…', [
    { label: 'a constant hum of overwhelm I just push through', score: -3, leans: 'numa:oizys' },
    { label: 'quietly telling myself "I\'ll fix the habit tomorrow" and not doing it', score: -3, leans: 'moyri:akrasia' },
    { label: 'holding steady enough either way', score: 0 },
    { label: 'reading my capacity and adjusting on purpose', score: 3 },
  ]),
  q('When your task list stalls out completely, is it more that…', [
    { label: 'you go inactive — you know what to do but cannot start', score: -3, leans: 'numa:aergia' },
    { label: 'your notifications turned into frenzied, reactive checking instead', score: -3, leans: 'hrmez:oistros' },
    { label: 'a bit of both, depending on the week', score: 0 },
    { label: 'neither — the list keeps moving', score: 2 },
  ]),
  q('A habit you were building broke three days ago. Now it feels…', [
    { label: 'gone for good — once broken, it never comes back the same', score: -3, leans: 'moyri:adrasteia' },
    { label: "your inbox and messages have piled into pure noise since then, too", score: -3, leans: 'hrmez:homados' },
    { label: 'annoying but recoverable', score: 0 },
    { label: 'a normal blip you restart from', score: 2 },
  ]),
  q('Week over week, your output has been…', [
    { label: 'quietly eroding, and I only notice looking back', score: -2, leans: 'numa:phthisis' },
    { label: 'sliding into a worse default without me deciding that', score: -2, leans: 'moyri:kakia' },
    { label: 'roughly flat', score: 0 },
    { label: 'trimmed deliberately to what capacity allows', score: 2 },
  ]),
  q('A demanding stretch hits and your first response is to…', [
    { label: 'push through and crash later', score: -2, leans: 'numa:oizys' },
    { label: 'check every ping the second it arrives, just in case', score: -2, leans: 'hrmez:oistros' },
    { label: 'sometimes notice the strain, sometimes not', score: 0 },
    { label: 'trim the plan to what the tank can hold', score: 3 },
  ]),
  q('You know a habit is the right one and still do not do it — how often?', [
    { label: 'most days — knowing it does nothing for actually doing it', score: -3, leans: 'moyri:akrasia' },
    { label: 'the stall shows up as just not starting anything at all', score: -3, leans: 'numa:aergia' },
    { label: 'sometimes', score: 0 },
    { label: 'rarely — knowing and doing line up', score: 2 },
  ]),
  q('Your inbox/messages right now would be described as…', [
    { label: 'noise — I cannot find the signal in it anymore', score: -3, leans: 'hrmez:homados' },
    { label: "a habit I keep meaning to fix feels permanently out of reach", score: -3, leans: 'moyri:adrasteia' },
    { label: 'cluttered but workable', score: 0 },
    { label: 'near zero and current', score: 2 },
  ]),
  q('When you are running low, do you notice before or after it costs you?', [
    { label: 'after — the erosion is invisible until the damage is done', score: -2, leans: 'numa:phthisis' },
    { label: "and even when I notice, my inbox is already noise by then", score: -2, leans: 'hrmez:homados' },
    { label: 'roughly at the same time', score: 0 },
    { label: 'before — I read capacity ahead of committing', score: 2 },
  ]),
  q('A bad habit slipping back in feels, to you, like…', [
    { label: "the new normal — it just quietly becomes how I operate", score: -2, leans: 'moyri:kakia' },
    { label: 'going idle rather than actively bad — I just stop', score: -2, leans: 'numa:aergia' },
    { label: 'a nuisance either way', score: 0 },
    { label: 'a one-off, corrected fast', score: 2 },
  ]),
  q('A ping arrives while you are deep in something. You…', [
    { label: 'drop everything and check — I cannot help it', score: -3, leans: 'hrmez:oistros' },
    { label: 'let the habit slide once more without deciding to', score: -3, leans: 'moyri:akrasia' },
    { label: 'notice it and decide later', score: 0 },
    { label: 'let it wait until the block ends', score: 2 },
  ]),
  q('Do you read your capacity before committing to more work?', [
    { label: 'never — I say yes and find out later', score: -2, leans: 'numa:oizys' },
    { label: 'occasionally', score: 0 },
    { label: 'every block', score: 2 },
  ]),
  q('A streak breaks. Your instinct about restarting it is…', [
    { label: '"why bother, it is already ruined"', score: -3, leans: 'moyri:adrasteia' },
    { label: 'unsure', score: 0 },
    { label: '"restart today, the streak is not the point"', score: 2 },
  ]),
  q('Slow decline vs. sudden crash — which matches your last burnout?', [
    { label: 'slow — I did not see it coming until it had already cost weeks', score: -2, leans: 'numa:phthisis' },
    { label: 'a mix', score: 0 },
    { label: 'neither — I catch the slide early', score: 2 },
  ]),
  q('Bad defaults (skipping the workout, doom-scrolling, etc.) feel, to you, like…', [
    { label: 'just what I do now — not even a decision anymore', score: -3, leans: 'moyri:kakia' },
    { label: 'a habit I fight sometimes', score: 0 },
    { label: 'rare, and I catch myself fast', score: 2 },
  ]),
  q('You open your inbox and the first feeling is…', [
    { label: 'dread at the noise — I do not know where to start', score: -3, leans: 'hrmez:homados' },
    { label: 'mild reluctance', score: 0 },
    { label: 'calm — it stays near empty', score: 2 },
  ]),
  q('Do notifications drive your schedule, or does your schedule drive when you check them?', [
    { label: 'notifications drive it completely — I am always reacting', score: -3, leans: 'hrmez:oistros' },
    { label: 'somewhere in between', score: 0 },
    { label: 'my schedule sets the windows, not the pings', score: 3 },
  ]),
  q('You cannot start a task even though nothing is stopping you — how often?', [
    { label: 'often — I just freeze', score: -3, leans: 'numa:aergia' },
    { label: 'sometimes', score: 0 },
    { label: 'rarely', score: 2 },
  ]),
  q('Would recovering a habit you broke last month feel realistic?', [
    { label: 'no — it feels permanently lost', score: -2, leans: 'moyri:adrasteia' },
    { label: 'maybe', score: 0 },
    { label: 'yes, absolutely', score: 2 },
  ]),
  q('When your energy is genuinely low, do you plan around it or ignore it?', [
    { label: 'ignore it and pay for it later', score: -2, leans: 'numa:oizys' },
    { label: 'half-plan around it', score: 0 },
    { label: 'plan around it deliberately', score: 2 },
  ]),
  q('Overall, the thing quietly running your regulation right now is…', [
    { label: 'whichever bad habit or noisy inbox got there first', score: -3, leans: 'hrmez:homados' },
    { label: 'a mix of capacity and habit, unmanaged', score: 0 },
    { label: 'a deliberate read of energy and habit both', score: 3 },
  ]),
];

// ── DIRECT — KYRS (koalemos/oknos), KRNOS (chaos), JANUS (aporia), TKNE (amechania/atlas)
const DIRECT_QUESTIONS: GearIntakeQuestion[] = [
  q('When a lot competes for your attention, what actually wins is…', [
    { label: 'whatever is loudest, and it turns out later it did not matter', score: -3, leans: 'kyrs:koalemos' },
    { label: 'a decision I keep circling and never actually close', score: -3, leans: 'janus:aporia' },
    { label: 'a rough muddle-through', score: 0 },
    { label: 'the thing that actually matters most, fast', score: 3 },
  ]),
  q('Your week, looked at honestly, is…', [
    { label: 'chaos — no realistic shape survives the first Tuesday', score: -3, leans: 'krnos:chaos' },
    { label: 'so overloaded with projects I am buried under the weight of it', score: -3, leans: 'tkne:atlas' },
    { label: 'roughly holding, with some slippage', score: 0 },
    { label: 'realistic and adjusted on purpose when reality shifts', score: 3 },
  ]),
  q('You put in real hours but the project shows nothing for it — why?', [
    { label: 'effort keeps getting undone before it counts, like it never happened', score: -3, leans: 'kyrs:oknos' },
    { label: "I genuinely do not know the next concrete step, so I flail", score: -3, leans: 'tkne:amechania' },
    { label: 'some of both', score: 0 },
    { label: 'that does not happen — effort compounds', score: 2 },
  ]),
  q('Loud, low-priority items versus your real priorities…', [
    { label: 'the loud ones win, almost every time', score: -3, leans: 'kyrs:koalemos' },
    { label: 'my calendar cannot hold either — it is just chaos', score: -3, leans: 'krnos:chaos' },
    { label: 'it varies', score: 0 },
    { label: 'I work the list top-down regardless of volume', score: 3 },
  ]),
  q('A decision that matters has been sitting unresolved for…', [
    { label: 'weeks, because I cannot close it', score: -3, leans: 'janus:aporia' },
    { label: 'a while, because I do not know the actual next move', score: -3, leans: 'tkne:amechania' },
    { label: 'a few days, normally', score: 0 },
    { label: 'not long — decisions get resolved and stay closed', score: 2 },
  ]),
  q('Busywork you know is not really moving anything — how often do you do it anyway?', [
    { label: 'constantly — it undoes itself before it compounds', score: -3, leans: 'kyrs:oknos' },
    { label: 'my project load is so heavy I cannot tell what compounds anymore', score: -3, leans: 'tkne:atlas' },
    { label: 'sometimes', score: 0 },
    { label: 'rarely — I catch it and redirect', score: 2 },
  ]),
  q('Reality changes mid-week (something falls through, something new lands). You…', [
    { label: "the schedule was already fiction, so nothing really changes", score: -2, leans: 'krnos:chaos' },
    { label: "the loud thing that showed up just takes over regardless of priority", score: -2, leans: 'kyrs:koalemos' },
    { label: 'scramble to adjust', score: 0 },
    { label: 'reschedule deliberately and keep the week realistic', score: 2 },
  ]),
  q('A stalled project — is the block "I do not know what to do" or "I have too much to do"?', [
    { label: '"I do not know what to do" — it is formless', score: -3, leans: 'tkne:amechania' },
    { label: '"I cannot even see it under everything else"', score: -3, leans: 'janus:aporia' },
    { label: 'a bit of both', score: 0 },
    { label: 'neither — projects have a clear next action', score: 2 },
  ]),
  q('Do your tasks get scored or ranked at all?', [
    { label: 'no — nothing is ranked, so volume wins', score: -2, leans: 'kyrs:koalemos' },
    { label: 'informally', score: 0 },
    { label: 'yes, and I trust the ranking', score: 2 },
  ]),
  q('The number of active projects you are carrying right now feels…', [
    { label: 'crushing — I am buried under the sheer weight of it', score: -3, leans: 'tkne:atlas' },
    { label: "and my schedule has no real structure to hold any of them", score: -3, leans: 'krnos:chaos' },
    { label: 'heavy but manageable', score: 0 },
    { label: 'sized to what I can actually run', score: 2 },
  ]),
  q('A fork between two real options — you tend to…', [
    { label: 'stall indefinitely, unable to close it', score: -3, leans: 'janus:aporia' },
    { label: 'pick whichever is loudest, not whichever matters', score: -3, leans: 'kyrs:koalemos' },
    { label: 'take a while but eventually decide', score: 0 },
    { label: 'decide and move — the choice stays closed', score: 2 },
  ]),
  q('Do you build and follow a realistic week, or does the week just happen to you?', [
    { label: 'it just happens — there is no real structure underneath it', score: -3, leans: 'krnos:chaos' },
    { label: 'somewhere in between', score: 0 },
    { label: 'I build it and reschedule deliberately when it breaks', score: 3 },
  ]),
  q('Effort on a project that quietly gets undone (skipped, redone, wasted) happens…', [
    { label: 'often — progress does not stick', score: -2, leans: 'kyrs:oknos' },
    { label: 'occasionally', score: 0 },
    { label: 'rarely — progress holds', score: 2 },
  ]),
  q('When a project has no clear next action, you…', [
    { label: 'freeze — I genuinely do not know where to start', score: -3, leans: 'tkne:amechania' },
    { label: 'poke at it without a plan', score: 0 },
    { label: 'structure it from outcome to milestone to next action', score: 2 },
  ]),
  q('How many things are you carrying that you have quietly stopped tracking properly?', [
    { label: 'too many — the weight of it means some just get dropped silently', score: -3, leans: 'tkne:atlas' },
    { label: 'a couple', score: 0 },
    { label: 'none — everything active has a home', score: 2 },
  ]),
  q('A trivial task and an important one land at the same time. Which gets done first?', [
    { label: 'the trivial one — it was just louder', score: -2, leans: 'kyrs:koalemos' },
    { label: 'depends on mood', score: 0 },
    { label: 'the important one, every time', score: 2 },
  ]),
  q('Indecision on something small — how long does it usually drag on?', [
    { label: 'far longer than the decision deserves', score: -2, leans: 'janus:aporia' },
    { label: 'a normal amount of time', score: 0 },
    { label: "it doesn't — small decisions close fast", score: 2 },
  ]),
  q('Your calendar for next week — does it resemble what will actually happen?', [
    { label: 'not even close', score: -3, leans: 'krnos:chaos' },
    { label: 'roughly', score: 0 },
    { label: 'yes, closely, because it gets adjusted as reality moves', score: 2 },
  ]),
  q('Taking on one more project right now would feel like…', [
    { label: 'the thing that finally breaks the pile', score: -2, leans: 'tkne:atlas' },
    { label: 'a stretch, but doable', score: 0 },
    { label: 'fine — there is real room for it', score: 2 },
  ]),
  q('Overall, when Direct breaks down for you, it is mostly…', [
    { label: 'the wrong things winning your attention', score: -2, leans: 'kyrs:koalemos' },
    { label: 'a schedule that never held any shape to begin with', score: -2, leans: 'krnos:chaos' },
    { label: 'a mix, unmanaged', score: 0 },
    { label: 'not really a problem — Direct mostly holds', score: 2 },
  ]),
];

// ── EXECUTE — METIS (ate), THEMIS (dysnomia)
const EXECUTE_QUESTIONS: GearIntakeQuestion[] = [
  q('The live day gets disrupted (a call, an emergency, a surprise). You…', [
    { label: 'make a rash, in-the-moment call that blows up the rest of the day', score: -3, leans: 'metis:ate' },
    { label: 'roll with it as best I can', score: 0 },
    { label: 'absorb it and resume the plan deliberately', score: 3 },
  ]),
  q('A routine you run repeatedly comes out…', [
    { label: 'different every time — no rule holds', score: -3, leans: 'themis:dysnomia' },
    { label: 'roughly the same', score: 0 },
    { label: 'identically, on rails', score: 3 },
  ]),
  q('Mid-crisis, your decisions tend to be…', [
    { label: 'reactive and, in hindsight, ruinous', score: -3, leans: 'metis:ate' },
    { label: 'okay under the circumstances', score: 0 },
    { label: 'still lined up with the plan, even under pressure', score: 2 },
  ]),
  q('Mid-routine, when something interrupts you…', [
    { label: 'I skip steps or abandon it entirely', score: -3, leans: 'themis:dysnomia' },
    { label: 'I usually get back to it', score: 0 },
    { label: 'I resume and finish the sequence exactly', score: 2 },
  ]),
  q('Right now, an unplanned disruption in your day is more likely to…', [
    { label: 'trigger a rash call I regret by evening', score: -3, leans: 'metis:ate' },
    { label: 'derail whatever routine I was mid-way through', score: -3, leans: 'themis:dysnomia' },
    { label: 'cost me some time but not much else', score: 0 },
    { label: 'get absorbed without breaking anything', score: 3 },
  ]),
  q('Do you follow a saved protocol or improvise each time?', [
    { label: 'improvise from memory, every time', score: -2, leans: 'themis:dysnomia' },
    { label: 'a bit of both', score: 0 },
    { label: 'run the saved protocol', score: 2 },
  ]),
  q('Looking back at a disrupted day, the choice that caused the most damage was made…', [
    { label: 'in a blind, rushed moment', score: -2, leans: 'metis:ate' },
    { label: 'under some pressure but not blind', score: 0 },
    { label: 'there was no such moment — the day held', score: 2 },
  ]),
  q('A protocol step gets silently skipped under pressure — how often?', [
    { label: 'often, and I usually do not notice until later', score: -3, leans: 'themis:dysnomia' },
    { label: 'occasionally', score: 0 },
    { label: 'never — the protocol runs complete or not at all', score: 2 },
  ]),
  q('When the live agenda gets thrown off, your instinct is to…', [
    { label: 'panic-react and make it worse', score: -2, leans: 'metis:ate' },
    { label: 'pause and figure it out', score: 0 },
    { label: 'follow the recovery step you already planned for this', score: 2 },
  ]),
  q('Two versions of the same routine, run a week apart, would look…', [
    { label: 'nothing alike', score: -2, leans: 'themis:dysnomia' },
    { label: 'similar but not identical', score: 0 },
    { label: 'essentially identical', score: 2 },
  ]),
  q('A high-stakes moment lands without warning. Your first move is usually…', [
    { label: 'whatever feels urgent in the second, consequences later', score: -3, leans: 'metis:ate' },
    { label: 'a quick gut check, then act', score: 0 },
    { label: 'a beat to check the plan, then act', score: 2 },
  ]),
  q('After a disrupted day, how much of the original plan actually got done?', [
    { label: 'almost none — the disruption ate the whole day', score: -2, leans: 'metis:ate' },
    { label: 'about half', score: 0 },
    { label: 'most of it — the plan resumed', score: 2 },
  ]),
  q('A step you always skip in a given routine — do you know which one it is?', [
    { label: "there's more than one, and I only notice by the result being wrong", score: -2, leans: 'themis:dysnomia' },
    { label: 'roughly, yes', score: 0 },
    { label: 'no skipped steps — I know the protocol cold', score: 2 },
  ]),
  q('In the moment, blindness to consequences shows up for you as…', [
    { label: 'acting first, realizing the cost after', score: -3, leans: 'metis:ate' },
    { label: 'occasionally', score: 0 },
    { label: 'rarely — consequences get weighed live', score: 2 },
  ]),
  q('Do two people running your protocol from your notes get the same result?', [
    { label: 'no — the notes do not actually pin the routine down', score: -2, leans: 'themis:dysnomia' },
    { label: 'roughly', score: 0 },
    { label: 'yes, reliably', score: 2 },
  ]),
  q('A deliberately disrupted day (a drill, a stress test) would find you…', [
    { label: 'making a call you would not stand behind tomorrow', score: -2, leans: 'metis:ate' },
    { label: 'muddling through', score: 0 },
    { label: 'resuming the agenda each time, clean', score: 2 },
  ]),
  q('The gap between "the protocol as written" and "the protocol as run" is…', [
    { label: 'large — what actually happens barely resembles the doc', score: -2, leans: 'themis:dysnomia' },
    { label: 'some drift', score: 0 },
    { label: 'basically none', score: 2 },
  ]),
  q('When you are caught off guard, the cost usually lands…', [
    { label: 'immediately, from a rash call in the moment', score: -2, leans: 'metis:ate' },
    { label: 'somewhere down the line', score: 0 },
    { label: 'nowhere — it gets absorbed', score: 2 },
  ]),
  q('A step-heavy task you do often — could you run it blind, from memory, correctly?', [
    { label: 'no, it would come out wrong', score: -2, leans: 'themis:dysnomia' },
    { label: 'mostly', score: 0 },
    { label: 'yes, exactly', score: 2 },
  ]),
  q('Overall, execution breaks down for you mostly through…', [
    { label: 'rash calls under live pressure', score: -2, leans: 'metis:ate' },
    { label: 'routines that will not hold their shape', score: -2, leans: 'themis:dysnomia' },
    { label: 'a mix', score: 0 },
    { label: "it doesn't, really — execution mostly holds", score: 2 },
  ]),
];

// ── REVISE — CLIO (lethe), ARGOS (achlys/apate), NEMOS (akosmia)
const REVISE_QUESTIONS: GearIntakeQuestion[] = [
  q('After a rough stretch, the lesson…', [
    { label: 'is forgotten; I repeat the mistake', score: -3, leans: 'clio:lethe' },
    { label: 'the review itself was too foggy to actually see what happened', score: -3, leans: 'argos:achlys' },
    { label: 'sometimes sticks', score: 0 },
    { label: 'gets pulled and changes what I do', score: 3 },
  ]),
  q('Your week-in-review numbers versus what actually happened…', [
    { label: "I round the story in my favor, and I know it", score: -3, leans: 'argos:apate' },
    { label: "my notes from that week are scattered — I can't find them now", score: -3, leans: 'nemos:akosmia' },
    { label: 'roughly line up', score: 0 },
    { label: 'match honestly, even when it is not flattering', score: 2 },
  ]),
  q('Your morning + night sessions…', [
    { label: 'rarely happen, so nothing gets carried forward', score: -3, leans: 'clio:lethe' },
    { label: 'happen, but old notes are impossible to relocate afterward', score: -3, leans: 'nemos:akosmia' },
    { label: 'happen some days', score: 0 },
    { label: 'bookend the day reliably', score: 3 },
  ]),
  q('Reviewing a bad week honestly versus explaining it away…', [
    { label: 'I explain it away most of the time', score: -3, leans: 'argos:apate' },
    { label: 'the review is too foggy to even get that far', score: -3, leans: 'argos:achlys' },
    { label: 'a mix', score: 0 },
    { label: 'I look at it straight, no spin', score: 2 },
  ]),
  q('Do reflections turn into actual changes elsewhere?', [
    { label: 'no, just venting — nothing changes downstream', score: -3, leans: 'clio:lethe' },
    { label: 'occasionally', score: 0 },
    { label: 'yes — a habit or protocol edit follows', score: 2 },
  ]),
  q('Finding a note or reference from a review two months ago is…', [
    { label: 'basically impossible — things are filed nowhere consistent', score: -3, leans: 'nemos:akosmia' },
    { label: 'the memory of it has already faded regardless', score: -3, leans: 'clio:lethe' },
    { label: 'doable with some digging', score: 0 },
    { label: 'fast and easy', score: 2 },
  ]),
  q('Sitting down to review the week, the honest first feeling is…', [
    { label: 'fog — I genuinely cannot see the week clearly', score: -3, leans: 'argos:achlys' },
    { label: 'a bit selective about what I let myself look at', score: -3, leans: 'argos:apate' },
    { label: 'neutral', score: 0 },
    { label: 'clarity — the shape of the week is obvious', score: 2 },
  ]),
  q('A mistake you made last month — would you catch yourself making it again this month?', [
    { label: 'no, and I probably already have', score: -3, leans: 'clio:lethe' },
    { label: 'maybe', score: 0 },
    { label: 'yes, it is flagged and avoided', score: 2 },
  ]),
  q('Your filing system for reviews and reflections is…', [
    { label: 'nonexistent — everything is scattered across notes and apps', score: -2, leans: 'nemos:akosmia' },
    { label: 'loosely organized', score: 0 },
    { label: 'consistent, and retrieval is fast', score: 2 },
  ]),
  q('When a review looks bad, do you soften the framing before writing it down?', [
    { label: 'yes, most of the time', score: -2, leans: 'argos:apate' },
    { label: 'sometimes', score: 0 },
    { label: 'no — it goes down as it actually happened', score: 2 },
  ]),
  q('A lesson you learned the hard way three months ago — is it written anywhere you could find it today?', [
    { label: "no, it lives only in vague memory, which has already faded", score: -3, leans: 'clio:lethe' },
    { label: 'maybe, somewhere', score: 0 },
    { label: 'yes, filed and findable', score: 2 },
  ]),
  q('Reviewing actual-vs-planned attention for the week, can you actually see the gap?', [
    { label: 'no — the picture is too foggy to compare honestly', score: -3, leans: 'argos:achlys' },
    { label: 'roughly', score: 0 },
    { label: 'yes, clearly, and I adjust next week from it', score: 2 },
  ]),
  q('How often does a review conclude "it went fine" when it clearly did not?', [
    { label: 'often — I talk myself into it', score: -2, leans: 'argos:apate' },
    { label: 'occasionally', score: 0 },
    { label: 'never — the review calls it straight', score: 2 },
  ]),
  q('Do duplicated or misfiled items pile up in your review notes?', [
    { label: 'constantly — nothing has one home', score: -2, leans: 'nemos:akosmia' },
    { label: 'a few', score: 0 },
    { label: 'no — zero misfiled items, consistently', score: 2 },
  ]),
  q('A recurring problem across several weeks — did you notice the pattern, or did each week feel new?', [
    { label: 'each week felt new — I never connected the dots', score: -2, leans: 'clio:lethe' },
    { label: 'I noticed eventually', score: 0 },
    { label: 'flagged after the second occurrence', score: 2 },
  ]),
  q('If you had to bet, is your self-review flattering-but-wrong, or accurate-but-uncomfortable?', [
    { label: 'flattering but wrong', score: -2, leans: 'argos:apate' },
    { label: 'somewhere in between', score: 0 },
    { label: 'accurate, even when uncomfortable', score: 2 },
  ]),
  q('Retrieving last quarter\'s reflections to check progress — how long does that take?', [
    { label: 'I genuinely could not find them', score: -3, leans: 'nemos:akosmia' },
    { label: 'a while, with effort', score: 0 },
    { label: 'minutes', score: 2 },
  ]),
  q('Reading a review from a month you barely remember, does it bring the week back clearly?', [
    { label: 'no — the fog is total, the notes do not help either', score: -2, leans: 'argos:achlys' },
    { label: 'partially', score: 0 },
    { label: 'yes, vividly', score: 2 },
  ]),
  q('When something goes wrong twice in a row, does the second time surprise you?', [
    { label: 'yes — the first time left no trace', score: -2, leans: 'clio:lethe' },
    { label: 'a little', score: 0 },
    { label: 'no — it was already flagged from the first time', score: 2 },
  ]),
  q('Overall, when Revise breaks down for you, it is mostly…', [
    { label: 'the lesson not sticking at all', score: -2, leans: 'clio:lethe' },
    { label: 'not being able to see the week clearly, or fudging it when I do', score: -2, leans: 'argos:apate' },
    { label: 'a mix', score: 0 },
    { label: 'it does not, really — Revise mostly holds', score: 2 },
  ]),
];

export const GEAR_INTAKE: Record<Gear, GearIntakeQuestion[]> = {
  orient: ORIENT_QUESTIONS,
  regulate: REGULATE_QUESTIONS,
  direct: DIRECT_QUESTIONS,
  execute: EXECUTE_QUESTIONS,
  revise: REVISE_QUESTIONS,
};

/**
 * Fold a gear table's chosen answers into the gear's most-active malady
 * (tool + subtype) and a starting position. Most-voted `leans` pair wins;
 * ties break toward the more-negative average score among the tied pairs;
 * if nothing was tagged at all, fall back to the gear's default DMT and its
 * first malady candidate.
 */
export function foldGearIntake(gear: Gear, chosen: GearIntakeOption[]): GearIntakeResult {
  const avg = chosen.length ? chosen.reduce((a, o) => a + o.score, 0) / chosen.length : 0;
  const position = Math.max(-4, Math.min(4, Math.round(avg)));

  const votes = new Map<string, number>();
  const sums = new Map<string, number>();
  for (const o of chosen) {
    if (!o.leans) continue;
    votes.set(o.leans, (votes.get(o.leans) ?? 0) + 1);
    sums.set(o.leans, (sums.get(o.leans) ?? 0) + o.score);
  }

  let winner: string | null = null;
  let bestVotes = 0;
  let bestAvg = Infinity;
  for (const [pair, n] of votes) {
    const pairAvg = sums.get(pair)! / n;
    if (n > bestVotes || (n === bestVotes && pairAvg < bestAvg)) {
      winner = pair;
      bestVotes = n;
      bestAvg = pairAvg;
    }
  }

  let toolId: string;
  let subtype: string;
  if (winner) {
    const [t, s] = winner.split(':');
    toolId = t;
    subtype = normalizeMaladyId(t, s); // old rows: nemos:dysnomia -> akosmia
  } else {
    toolId = GEAR_DEFAULT_DMT[gear];
    const tool = DMT_TOOLS.find((t) => t.id === toolId);
    subtype = tool?.maladies[0]?.id ?? '';
  }

  return { gear, toolId, subtype, position, depth: chosen.length };
}
